import { useCallback, useRef, useState } from 'react';
import { useInfiniteScroll } from './useInfiniteScroll';

/**
 * 共用分頁列表狀態：page/totalPages/isLoading、page 1 覆蓋、page > 1 去重後 append，
 * 並內建掛載 useInfiniteScroll。四個列表頁（首頁一般/標籤模式、收藏、搜尋、偶像頁）共用此邏輯。
 *
 * 注意：
 * - fetchPage 需自行 try/catch，例外不會被這裡吞掉
 * - reset() 不會自動清空畫面上的舊資料，需等新 request resolve 才覆蓋（與現有各頁行為一致）；
 *   若該頁需要「切換時先清空」（例如偶像頁切換偶像避免殘留閃現），請呼叫端自行先呼叫 setData([]) 再 reset()
 *
 * @param {Function} fetchPage async (pageNum) => { data, total } | null，null 代表非成功狀態碼，本頁不處理
 * @param {Function} getId     從單筆資料取得去重用的唯一 id
 * @param {number}   [limit=10]
 */
export function usePaginatedList({ fetchPage, getId, limit = 10 }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);

  // fetchPage 每次 render 都可能是新的閉包（依賴當前 params），用 ref 讓 load 永遠呼叫最新版本
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  // 防止篩選條件快速連續切換時，較舊的 request 較晚 resolve 反而蓋掉較新的資料
  const requestSeqRef = useRef(0);

  const load = useCallback(async (pageNum) => {
    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    try {
      const result = await fetchPageRef.current(pageNum);
      if (seq !== requestSeqRef.current || !result) return;
      const { data: newData, total } = result;
      if (pageNum === 1) {
        setData(newData);
      } else {
        setData(prev => {
          const prevIds = new Set(prev.map(getId));
          const uniqueNewData = newData.filter(item => !prevIds.has(getId(item)));
          return [...prev, ...uniqueNewData];
        });
      }
      setTotalPages(Math.ceil(total / limit));
      setPage(pageNum);
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  }, [getId, limit]);

  // 重置到第一頁（切換篩選條件、新增/刪除內容後刷新等）
  const reset = useCallback(() => load(1), [load]);

  useInfiniteScroll(() => {
    if (isLoading) return;
    if (totalPages !== null && page < totalPages) {
      load(page + 1);
    }
  });

  return { data, setData, isLoading, page, totalPages, reset };
}
